<template>
  <div
      v-if="open"
      class="overlay"
      role="dialog"
      aria-modal="true"

      aria-labelledby="frontsheet-title"
      @keydown.esc.stop.prevent="emitClose"
  >
    <div class="dialog">
      <header class="dialog-header">
        <h2
            id="frontsheet-title"
            class="h1"
        >
          Vorblatt erfassen
          <span class="text-small font-normal">für {{ student.first_name }} {{ student.last_name }}</span>
        </h2>
        <button
            class="btn icon-only"
            type="button"
            aria-label="Schließen"
            @click="emitClose"
        >
          <i
              class="pi pi-times"
              aria-hidden="true"
          />
        </button>
      </header>

      <section class="dialog-body">
        <div
            v-if="loading"
            class="notice mt-12"
            role="status"
        >
          Lädt Voreinstellungen …
        </div>

        <form
            v-else
            class="form"
            @submit.prevent="submit"
        >
          <div class="field">
            <label for="fs-studentname">alternativer Schülername</label>
            <input
                id="fs-studentname"
                v-model.trim="model.studentname"
                type="text"
                class="input"
                autocomplete="off"
            >
          </div>
          <div class="two-col">
            <div class="field">
              <label for="fs-birthday">Geburtstag</label>
              <flat-pickr
                  id="fs-birthday"
                  v-model="model.birthday"
                  :config="datePickerConfig"
                  class="input"
                  aria-label="Geburtstag auswählen"
              />
            </div>

            <div class="field">
              <label for="fs-place">Ort</label>
              <input
                  id="fs-place"
                  v-model.trim="model.place"
                  type="text"
                  class="input"
                  autocomplete="address-level2"
                  placeholder="z. B. Musterstadt"
              >
            </div>
          </div>

          <div class="two-col">
            <div class="field">
              <label for="fs-grade">Jahrgang</label>
              <input
                  id="fs-grade"
                  v-model.trim="model.grade"
                  type="text"
                  class="input"
                  inputmode="numeric"
                  placeholder="z. B. 5, 6, 7 …"
              >
            </div>

            <div class="field">
              <label for="fs-schoolyear">Schuljahr</label>
              <input
                  id="fs-schoolyear"
                  v-model.trim="model.school_year"
                  type="text"
                  class="input"
                  placeholder="z. B. 2025/26"
              >
            </div>
          </div>

          <div class="field">
            <label for="fs-notes">Notizen</label>
            <textarea
                id="fs-notes"
                v-model.trim="model.notes"
                class="input"
                rows="4"
                placeholder="Freitext / Hinweise"
            />
          </div>
        </form>
      </section>

      <footer class="dialog-footer">
        <button
            class="btn"
            type="button"
            @click="emitClose"
        >
          Abbrechen
        </button>
        <button
            class="btn btn-primary"
            type="button"
            :disabled="saving || !isValid"
            @click="submit"
        >
          <span
              v-if="saving"
              class="spinner"
              aria-hidden="true"
          />
          Speichern
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {computed, reactive, watch, ref} from 'vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const props = defineProps({
  open: {type: Boolean, default: false},
  student: {type: Object, default: null},
  initial: {type: Object, default: null},
  loading: {type: Boolean, default: false}
});

const emit = defineEmits(['close', 'save']);

const model = reactive({
  studentname: '',
  birthday: '',
  place: '',
  grade: '',
  school_year: '',
  notes: ''
});

const saving = ref(false);

watch(() => props.initial, (val) => {
  const src = val && typeof val === 'object' ? val : {};
  model.studentname = src.studentname ?? '';
  model.birthday = src.birth_date ?? '';
  model.place = src.place ?? '';
  model.grade = src.class_level ?? '';
  model.school_year = src.school_year ?? '';
  model.notes = src.notes ?? '';
}, {immediate: true});

const datePickerConfig = computed(() => ({
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: German,
  allowInput: true
}));

// --- Validierung (alle Felder sind Pflicht) ---
const touched = reactive({
  birthday: false,
  place: false,
  grade: false,
  school_year: false,
  notes: false
});

const errors = reactive({
  birthday: '',
  place: '',
  grade: '',
  school_year: '',
  notes: ''
});

function markTouched(name) {
  if (Object.prototype.hasOwnProperty.call(touched, name)) {
    touched[name] = true;
  }
}

function validateField(name) {
  switch (name) {
    case 'birthday': {
      const v = String(model.birthday || '').trim();
      if (!v) {
        errors.birthday = 'Bitte Geburtstag angeben.';
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        errors.birthday = 'Ungültiges Datum. Bitte TT.MM.JJJJ auswählen.';
      } else {
        errors.birthday = '';
      }
      break;
    }
    case 'place': {
      const v = String(model.place || '').trim();
      errors.place = v ? '' : 'Bitte Ort angeben.';
      break;
    }
    case 'grade': {
      const v = String(model.grade || '').trim();
      if (!v) {
        errors.grade = 'Bitte Jahrgang angeben.';
      } else if (!/^\d+$/.test(v)) {
        errors.grade = 'Nur Ziffern erlaubt.';
      } else {
        errors.grade = '';
      }
      break;
    }
    case 'school_year': {
      const v = String(model.school_year || '').trim();
      errors.school_year = v ? '' : 'Bitte Schuljahr angeben.';
      break;
    }
    case 'notes': {
      const v = String(model.notes || '').trim();
      errors.notes = v ? '' : 'Bitte Notizen ausfüllen.';
      break;
    }
    default:
      break;
  }
}

function validateAll(update = true) {
  const fields = ['birthday', 'place', 'grade', 'school_year', 'notes'];
  if (update) {
    fields.forEach(f => validateField(f));
  } else {
    // Nur prüfen, ohne errors zu überschreiben (schnelle Ableitung)
    const vBirthday = !!String(model.birthday || '').trim() && /^\d{4}-\d{2}-\d{2}$/.test(String(model.birthday || '').trim());
    const vPlace = !!String(model.place || '').trim();
    const vGrade = !!String(model.grade || '').trim() && /^\d+$/.test(String(model.grade || '').trim());
    const vSy = !!String(model.school_year || '').trim();
    const vNotes = !!String(model.notes || '').trim();
    return vBirthday && vPlace && vGrade && vSy && vNotes;
  }
  return Object.values(errors).every(e => !e);
}

const isValid = computed(() => validateAll(false));

function firstInvalidField() {
  if (!String(model.birthday || '').trim() || !!errors.birthday) return 'fs-birthday';
  if (!String(model.place || '').trim() || !!errors.place) return 'fs-place';
  if (!String(model.grade || '').trim() || !!errors.grade) return 'fs-grade';
  if (!String(model.school_year || '').trim() || !!errors.school_year) return 'fs-schoolyear';
  if (!String(model.notes || '').trim() || !!errors.notes) return 'fs-notes';
  return null;
}

function emitClose() {
  if (saving.value) return;
  emit('close');
}

function submit() {
  if (saving.value) return;

  // Vollvalidierung und Touch setzen
  const fields = ['birthday', 'place', 'grade', 'school_year', 'notes'];
  fields.forEach(f => markTouched(f));
  const ok = validateAll(true);
  if (!ok) {
    // Erstes fehlerhaftes Feld fokussieren
    const id = firstInvalidField();
    if (id) {
      const el = document.getElementById(id);
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    }
    return;
  }

  saving.value = true;
  try {
    emit('save', {
      studentId: props.student?.id ?? null,
      data: {...model}
    });
  } finally {
    // Übergibt Kontrolle an Parent; Parent schließt und kann saving-Feedback übernehmen.
    saving.value = false;
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 16px;
}

.dialog {
  width: 100%;
  max-width: 760px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0;
}

.dialog-body {
  padding: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px 14px 14px;
  border-top: 1px solid var(--border);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 5px;
}

.field label {
  margin-bottom: 0;
}

.input {
  height: 36px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
}

textarea.input {
  height: auto;
  min-height: 150px;
  resize: vertical;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.btn.icon-only {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 999px;
  margin-right: 6px;
  animation: spin .9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.meta {
  color: var(--muted);
  font-size: 12px;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin-top: 4px;
}

.help.danger {
  color: var(--danger);
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}

.mt-12 {
  margin-top: 12px;
}
</style>
